import { useState, useContext, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';

import Modal from 'components/modal/delete';
import ChargeModal from 'components/modal/charge';

import {
  getPolysomnographyById,
  deletePolysomnography,
} from 'api/polysomnographies';
import { createStripeCustomer } from 'api/stripe';
import { generateDocuments } from 'api/documents';

import { UserContext } from 'contexts/user';
import { formatDatetime } from 'helpers/timezone';
import useWindowDimensions, {
  isMobileResolution,
} from 'helpers/useWindowDimensions';
import { nameDecorator } from 'helpers/name';
import { getFullName } from 'helpers/general';
import { getPaymentModalLabel } from 'helpers/customer';

import {
  STATUSES,
  RECORDING_STATUSES,
  NEXT_ACTIONS,
  MANUAL_PAYMENT_STATUSES,
} from 'config/polysomnography';

import CustomerCard from 'components/cards/customer';
import TariffsCard from 'components/cards/tariffs';
import PaymentIntentsCard from 'components/cards/payment_intents';
import DocumentsCard from 'components/cards/documents';
import ViewField from 'components/view_field/view_field';
import NoAccess from 'pages/no_access';

import Deleted from 'components/deleted/deleted';

const PolysomnographiesItem = () => {
  const [loading, setLoading] = useState(true);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  const { id } = useParams();
  const { user } = useContext(UserContext);
  const history = useHistory();

  const { width } = useWindowDimensions();
  const isMobile = isMobileResolution(width);

  const [isChargeable, setIsChargeable] = useState(false);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [data, setData] = useState(null);
  const [deletedByData, setDeletedByData] = useState(null);

  const [customerFullName, setCustomerFullName] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);

    const polysomnography = await getPolysomnographyById(id);

    if (polysomnography.deletedAt && polysomnography.deletedBy) {
      setDeletedByData({
        email: polysomnography.deletedBy.email,
        deletedAt: polysomnography.deletedAt,
      });
    } else {
      const { customer } = polysomnography;
      setData(polysomnography);

      setCustomerFullName(
        nameDecorator(customer?.firstName, customer?.lastName),
      );

      const hasSucceededOrProcessingPayment =
        polysomnography?.paymentIntents?.some(
          (p) => p.status === 'succeeded' || p.status === 'processing',
        );

      setIsChargeable(!hasSucceededOrProcessingPayment);
    }

    setLoading(false);
  }, [id]);

  const onDelete = async () => {
    setLoading(true);

    await deletePolysomnography(id, { id: user.id });

    setDeleteModalOpen(false);
    history.goBack();
    setLoading(false);
  };

  const onGenerateDocuments = async () => {
    setDocumentsLoading(true);
    await generateDocuments(data.id, 'polysomnography');

    await fetchData();

    setDocumentsLoading(false);
  };

  const onCharge = async (doctorFee, patientFee) => {
    await createStripeCustomer({
      email: data.customer.email,
      name: `${customerFullName}`,
      doctorFee,
      patientFee,
      polysomnographyId: data.id,
    });

    const polysomnography = await getPolysomnographyById(id);
    setData(polysomnography);

    setChargeModalOpen(false);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (data?.statusCode === 403) return <NoAccess />;
  if (deletedByData) return <Deleted deletedBy={deletedByData} />;
  if (loading || !data)
    return (
      <main className="th-main">
        <section className="th-canvas"></section>
      </main>
    );

  const statusLabel = STATUSES.find((r) => r.value === data.status);
  const recordingStatusLabel = RECORDING_STATUSES.find(
    (r) => r.value === data.recordingStatus,
  );
  const nextActionLabel = NEXT_ACTIONS.find((r) => r.value === data.nextAction);
  const manualPaymentStatusLabel = MANUAL_PAYMENT_STATUSES.find(
    (r) => r.value === data.manualPaymentStatus,
  );

  const paymentLabel = getPaymentModalLabel(data.customer);

  return (
    <>
      <main className="th-main">
        <section className="th-canvas">
          <div className="flex justify-between items-center mb10 bt b--lightgrey-d1 pt8">
            <div>
              {data.customer && (
                <h3 className="f2 fw6 ma0 pa0">
                  {data.customer.firstName} {data.customer.lastName}
                  &apos;s polysomnography
                </h3>
              )}
            </div>
            <section className="view-actions">
              {!isMobile && (
                <button
                  className="btn-white mr5"
                  data-cy="generate-documents"
                  disabled={documentsLoading}
                  onClick={onGenerateDocuments}
                >
                  <span>Generate documents</span>
                </button>
              )}
              {isChargeable && (
                <button
                  className="btn-white mr5"
                  data-cy="charge"
                  onClick={() => setChargeModalOpen(true)}
                >
                  <span>Charge</span>
                </button>
              )}
              <button
                className="btn-green mr5"
                data-cy="edit"
                onClick={() =>
                  history.push(`/polysomnographies/${data.id}/edit`)
                }
              >
                <span>Edit</span>
              </button>

              <button
                className="btn-red"
                data-cy="delete"
                onClick={() => setDeleteModalOpen(true)}
              >
                <span>Delete</span>
              </button>
            </section>
          </div>

          <CustomerCard data={data.customer} />
          <ViewField
            label="Doctor"
            value={`${data.doctor?.lastName?.toUpperCase()} ${
              data.doctor?.firstName
            }`}
          />
          <ViewField
            label="Technician"
            value={getFullName(
              data.technician?.firstName,
              data.technician?.lastName,
            )}
          />
          <ViewField
            label="Created at"
            value={formatDatetime(data.createdAt)}
          />
          <ViewField
            label="Delivery date"
            value={formatDatetime(data.deliveryDate)}
          />
          <ViewField
            label="Pickup date"
            value={formatDatetime(data.pickupDate)}
          />
          <ViewField label="Notes" value={data.notes} />
          <ViewField
            label="Status"
            value={statusLabel ? statusLabel.label : data.status}
          />
          <ViewField
            label="Recording status"
            value={
              recordingStatusLabel
                ? recordingStatusLabel.label
                : data.recordingStatus
            }
          />
          <ViewField
            label="Next action"
            value={nextActionLabel ? nextActionLabel.label : data.nextAction}
          />

          <TariffsCard data={data.tariff ? [data.tariff] : []} />
          <PaymentIntentsCard data={data.paymentIntents} />

          <ViewField
            label="Manual Payment status"
            value={
              manualPaymentStatusLabel
                ? manualPaymentStatusLabel.label
                : data.manualPaymentStatus
            }
          />

          <DocumentsCard data={data.documents} />
        </section>
      </main>
      {deleteModalOpen && (
        <Modal
          title="Polysomnography delete"
          content={`Are you sure you want to delete the <b>Polysomnography</b> of <b>${customerFullName}</b>?`}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={onDelete}
          onClickOutside={() => setDeleteModalOpen(false)}
        />
      )}
      {chargeModalOpen && (
        <ChargeModal
          type="polysomnography"
          title="Charge patient for polysomnography"
          content={`Choose the amount to charge to <b>${customerFullName}</b> for this polysomnography.<br>${customerFullName} <b>${paymentLabel}</b>.`}
          customer={data.customer}
          doctor={data.doctor}
          onClose={() => setChargeModalOpen(false)}
          onClickOutside={() => setChargeModalOpen(false)}
          onCharge={(doctorFee, patientFee) => onCharge(doctorFee, patientFee)}
        />
      )}
    </>
  );
};

export default PolysomnographiesItem;
